import moment from 'moment'
import isEqual from 'lodash/isEqual'
import { getCurrentYear } from '@/utils/form'
import { decodeHTML } from '@/utils/dom'
import { TOO_MUCH_UPLOAD } from '@/messages/errors'

const ANSWER_TYPE_TEXT = 1
const ANSWER_TYPE_TEXTAREA = 2
const ANSWER_TYPE_DATE = 3
const ANSWER_TYPE_CHECK = 4
const ANSWER_TYPE_CHECK_IMAGE = 5
const ANSWER_TYPE_IMAGE = 6

const MAX_LENGTH_TEXT = 255
const MAX_LENGTH_TEXTAREA = 1000

/**
 * 質問管理用クラス
 */
export default class Question {
  constructor(question) {
    Object.keys(question).forEach(key => {
      this[key] = question[key]
    })

    this.oldAnswer = this.getInitialAnswer()
    this.answer = this.getInitialAnswer()

    this.error = ''
  }

  isAnswerUpdated() {
    return !isEqual(this.oldAnswer, this.answer)
  }

  updateAnswer() {
    this.answered = true
    this.oldAnswer = this.answer
  }

  get id() {
    return this.question_id
  }

  get isText() {
    return this.answer_type === ANSWER_TYPE_TEXT
  }

  get isTextarea() {
    return this.answer_type === ANSWER_TYPE_TEXTAREA
  }

  get isDate() {
    return this.answer_type === ANSWER_TYPE_DATE
  }

  get isCheck() {
    return this.answer_type === ANSWER_TYPE_CHECK
  }

  get isCheckImage() {
    return this.answer_type === ANSWER_TYPE_CHECK_IMAGE
  }

  get isImage() {
    return this.answer_type === ANSWER_TYPE_IMAGE
  }

  get isCheckSingle() {
    return (
      this.select_count_from === 1 &&
      this.select_count_to === 1
    )
  }

  getInitialAnswer() {
    switch (this.answer_type) {
      case ANSWER_TYPE_TEXT:
      case ANSWER_TYPE_TEXTAREA:
        return decodeHTML(this[this.answerKey])
      case ANSWER_TYPE_DATE:
        return this[this.answerKey] || moment(this.initial_date).format('YYYY-MM-DD')
      case ANSWER_TYPE_CHECK:
      case ANSWER_TYPE_CHECK_IMAGE:
        return this[this.answerKey]
          .filter(opt => opt.answer_checked)
          .map(opt => ({ option_id: opt.option_id }))
      case ANSWER_TYPE_IMAGE:
        return this[this.answerKey]
          .map(img => ({
            answer_image_id: img.answer_image_id,
            image_name: img.image_name,
            signed_url: img.signed_url
          }))
          .sort((a, b) => b.answer_image_id - a.answer_image_id)
      default:
        return ''
    }
  }

  get answerKey() {
    switch (this.answer_type) {
      case ANSWER_TYPE_TEXT:
        return 'answer_text'
      case ANSWER_TYPE_TEXTAREA:
        return 'answer_text_multi_line'
      case ANSWER_TYPE_DATE:
        return 'answer_date'
      case ANSWER_TYPE_CHECK:
      case ANSWER_TYPE_CHECK_IMAGE:
        return 'select_option'
      case ANSWER_TYPE_IMAGE:
        return 'answer_image'
      default:
        return ''
    }
  }

  get answerParam() {
    return {
      question_revision: this.question_revision,
      [this.answerKey]: this.answer
    }
  }

  get selectedOptions() {
    return this.select_option.filter(op => op.answer_checked)
  }

  /**
   * バリデーション
   * @returns {boolean} 有効か？
   */
  validate() {
    this.error = ''

    switch (this.answer_type) {
      case ANSWER_TYPE_TEXT:
        if (this.answer.length < 1 || this.answer.length > MAX_LENGTH_TEXT) {
          this.error = `回答は1〜${MAX_LENGTH_TEXT}文字以内で入力してください。`
          return false
        }
        return true
      case ANSWER_TYPE_TEXTAREA:
        if (
          this.answer.length < 1 ||
          this.answer.length > MAX_LENGTH_TEXTAREA
        ) {
          this.error = `回答は1〜${MAX_LENGTH_TEXTAREA}文字以内で入力してください。`
          return false
        }
        return true
      case ANSWER_TYPE_DATE: {
        const m = moment(this.answer, 'YYYY-MM-DD')
        if (!m.isValid()) {
          this.error = `有効な日付を入力してください。`
          return false
        }
        const currentYear = getCurrentYear()
        const fromYear = currentYear - 80
        const toYear = currentYear + 3
        const year = m.year()
        if (year < fromYear || year > toYear) {
          this.error = `${fromYear}〜${toYear}年の日付を入力してください。`
          return false
        }
        return true
      }
      case ANSWER_TYPE_CHECK:
      case ANSWER_TYPE_CHECK_IMAGE:
        if (
          this.answer.length < this.select_count_from ||
          this.answer.length > this.select_count_to
        ) {
          this.error = `回答は${this.select_count_from}〜${this.select_count_to}個以内で選択してください。`
          return false
        }
        return true
      case ANSWER_TYPE_IMAGE:
        if (this.answer.length < 1 || this.answer.length > 10) {
          this.error = TOO_MUCH_UPLOAD
          return false
        }
        return true
      default:
        return true
    }
  }
}
